/**
 * @file: index.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Reservation detail: the info panel (identity, editable arrival, customer +
 * vehicle + advisor/tags/customer-type/mileage rows, timestamps), the primary
 * CTA that advances the booking (confirm / arrive / create-order), the
 * structured items panel (add from the catalog / remove), and an optional
 * remarks card. Secondary transitions (cancel / no-show) fold into the header
 * overflow menu. Thin composition root.
 */

import { DefaultLoader } from '@euler/app/components/loaders/DefaultLoader';
import { NavHeaderMoreButton } from '@euler/app/components/nav/NavHeaderMoreButton';
import { LD } from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import { ReservationsNavParams } from '@euler/app/flows/reservations';
import { ReservationDangerButton } from '@euler/app/flows/reservations/pages/detail/components/ReservationDangerButton';
import { ReservationHeaderCard } from '@euler/app/flows/reservations/pages/detail/components/ReservationHeaderCard';
import { ReservationItemsPanel } from '@euler/app/flows/reservations/pages/detail/components/ReservationItemsPanel';
import { ReservationPrimaryCta } from '@euler/app/flows/reservations/pages/detail/components/ReservationPrimaryCta';
import { ReservationRemarksCard } from '@euler/app/flows/reservations/pages/detail/components/ReservationRemarksCard';
import {
  canCancelReservation,
  canDeleteReservation,
  primaryActionFor,
  secondaryTransitionsFor,
  useReservationActions,
} from '@euler/app/flows/reservations/pages/detail/functions/useReservationActions';
import { Center } from '@euler/components/Center';
import { DateTimePicker } from '@euler/components/DateTimePicker';
import { EmptyView } from '@euler/components/EmptyView';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useCachedQuery } from '@euler/functions/useCachedQuery';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { Reservation } from '@euler/model/reservation/Reservation';
import {
  ReservationItem,
  parseReservationItems,
} from '@euler/model/reservation/ReservationItem';
import { useServiceFactory } from '@euler/services/factory';
import { dateFromValue } from '@euler/utils/datetime';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { ScrollView, View } from 'react-native';

export const ReservationDetailScreen = wrapNavigatorScreen(
  ({ id }: { id: string }) => {
    const { rpx } = useResponsiveContext();
    const navigation =
      useNavigation<StackNavigationProp<ReservationsNavParams>>();
    const { reservationService } = useServiceFactory();

    const query = useCachedQuery<Reservation>(
      `reservations/${id}.json`,
      30,
      async () => await reservationService.get(id),
    );
    const reservation = query.state;

    // Mutations refresh this screen via `query.update` and broadcast the fresh
    // row (cancel on its own channel) so the list patches + recounts in place.
    const actions = useReservationActions(id, reservation, query.update);

    const status = reservation?.status;
    const primary = useMemo(
      () => (status ? primaryActionFor(status) : null),
      [status],
    );
    const secondary = useMemo(
      () => (status ? secondaryTransitionsFor(status) : []),
      [status],
    );

    // Secondary transitions (cancel / no-show) live in the header ⋯ menu, like
    // the lead detail. Hidden once the reservation has none (arrived/terminal).
    const { openMoreMenu } = actions;
    useEffect(() => {
      navigation.setOptions({
        headerRight: secondary.length
          ? () => (
              <NavHeaderMoreButton
                color={LD.ink}
                size={rpx(18)}
                onPress={() => openMoreMenu(secondary)}
                style={{ marginRight: rpx(8) }}
              />
            )
          : undefined,
      });
    }, [navigation, secondary, openMoreMenu, rpx]);

    const items = useMemo(
      () => parseReservationItems(reservation?.items),
      [reservation?.items],
    );
    // The picker returns asynchronously via a nav callback, so read the latest
    // items through a ref rather than a closure-captured array.
    const itemsRef = useRef(items);
    itemsRef.current = items;

    const { replaceItems } = actions;
    // Open the catalog seeded with the current items; the +/- edit the set in
    // place and the full edited set comes back via onPicked.
    const vehicleRef = reservation;
    const onAddItems = useCallback(() => {
      navigation.navigate('_pickReservationItems', {
        initialItems: itemsRef.current,
        vehicle: {
          licensePlateNo: vehicleRef?.licensePlateNo,
          vin: vehicleRef?.vin ?? undefined,
        },
        onPicked: (next: ReservationItem[]) => void replaceItems(next),
      });
    }, [navigation, replaceItems, vehicleRef]);

    if (query.loading && reservation == null) {
      return (
        <Center css={'flex: 1;'}>
          <DefaultLoader />
        </Center>
      );
    }
    if (reservation == null) {
      return <EmptyView error={query.error} onRetry={query.invalidate} />;
    }

    const terminal = primary == null;

    let arrivalDate = new Date();
    try {
      arrivalDate = dateFromValue(reservation.expectedArrivalAt);
    } catch {
      // Fall back to now when the stored value is unreadable.
    }

    return (
      <View style={{ flex: 1, backgroundColor: LD.canvas }}>
        <ScrollView
          contentContainerStyle={{ padding: rpx(12), paddingBottom: rpx(28) }}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
        >
          <ReservationHeaderCard
            reservation={reservation}
            onEditArrival={!terminal ? actions.arrival.open : undefined}
            onEditAgent={!terminal ? actions.editAgent : undefined}
            onAddTag={!terminal ? actions.addTag : undefined}
            onRemoveTag={!terminal ? actions.removeTag : undefined}
            onEditCustomerType={
              !terminal ? actions.editCustomerType : undefined
            }
            onSaveMileage={!terminal ? actions.saveMileage : undefined}
            onOpenOrder={actions.openOrder}
          />

          {primary ? (
            <ReservationPrimaryCta
              primary={primary}
              onPress={() => actions.runPrimary(primary)}
            />
          ) : null}

          <ReservationItemsPanel
            items={items}
            onAdd={!terminal ? onAddItems : undefined}
            onItemPress={!terminal ? actions.openItemActions : undefined}
          />

          {reservation.remark ? (
            <ReservationRemarksCard remark={reservation.remark} />
          ) : null}

          {canCancelReservation(reservation.status) ? (
            <ReservationDangerButton
              label="取消预约"
              onPress={actions.cancel}
            />
          ) : canDeleteReservation(reservation.status) ? (
            <ReservationDangerButton
              label="删除预约"
              onPress={actions.remove}
            />
          ) : null}
        </ScrollView>

        <DateTimePicker
          isVisible={actions.arrival.editing}
          mode="date"
          date={arrivalDate}
          onConfirm={date => void actions.arrival.save(date)}
          onCancel={actions.arrival.close}
          confirmTextIOS="确认"
          cancelTextIOS="关闭"
        />
      </View>
    );
  },
  {
    title: '预约详情',
  },
);
